// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import { ReactNode } from "react";
import { Location } from "react-router-dom";

import { ActionType, IActionTrend } from "actions";
import { DataState, MainPanel, TimePeriod, UserStateAWSError } from "types";
import { AggregationType, ImportFile } from "graphql/api";

// uses webpack definitions
declare const IS_CLOUD: boolean;

export enum ModalPanel {
  NONE,
  INPUT,
  OUTPUT,
  TREND,
  REPORT_SETTINGS,
  TRENDMONITOR_SETTINGS,
  CHARGER_SETTINGS,
  RFID,
  RFID_LINK,
  TARIFF,
  GLOBAL_TARIFF,
  START_TRANSACTION,
}

export interface IStateGuiModal {
  panel: ModalPanel;
  dataState: DataState;
}

export interface IStateGuiConfirmModal {
  isOpen: boolean;
  title?: string;
  message?: ReactNode;
  action?: ActionType;
  actionPayload?: any;
  location?: Location;
}

export interface IPeriodData {
  startDate?: number; // s epoch
  endDate?: number; // s epoch
  timePeriod: TimePeriod;
  zoomMin?: number | undefined;
  zoomMax?: number | undefined;
}

export interface IChargingSessionFilter {
  startDate?: number | undefined;
  endDate?: number | undefined;
  timePeriod?: TimePeriod;
  user?: string;
  chargerName?: string;
}

export interface IReportOptions {
  startDate?: number;
  endDate?: number;
}

export interface IReportState {
  requested: number; // ms, timestamp of request
  link?: string;
  error?: string;
}

export interface IReportData {
  requested: number; // ms, timestamp of request
  data?: any;
  error?: string;
}

export interface ITableSort {
  column: string;
  isSortAscending: boolean;
}

export interface IModalRfid {
  id: number | undefined;
  addedrfid: string | undefined;
  checkResult: string | undefined;
  saveError?: string;
}

export enum OcppCmdState {
  NONE = "none",
  NEW = "new",
  ISSUED = "issued",
  SENT = "sent",
  CONFIRMED = "confirmed",
}

export interface IChargerCmdState {
  timestampMs: number | undefined;
  chargerId: number | undefined;
  state: OcppCmdState;
  hasError: boolean;
  result?: string;
}

export interface IStateGuiLogin {
  email?: string;
  userStateAWSFeedback?: UserStateAWSError;
  userStateErrorInfo?: string;
  userErrorText?: string;
}

export interface IStateGui {
  // navigation items
  navigationIsOpen: boolean;
  isDropdownOpen: {
    [panel in MainPanel]?: boolean;
  };

  // modal panel and data
  modal: IStateGuiModal;
  modalInput: number | undefined;
  modalOutput: number | undefined;
  modalTrendData: IActionTrend | undefined;
  modalReport: number | undefined;
  modalTrendMonitor: number | undefined;
  modalCharger: number | undefined; // charger ocpp id (not index)
  modalRfid: IModalRfid | undefined;
  modalTariff: number | undefined;

  // other items
  confirmModal: IStateGuiConfirmModal;
  tariffId: number;
  trendPeriod: IPeriodData;
  ioPeriod: IPeriodData;
  selectedTimestamp?: number; // timestamp of selected trend data
  selectedIoType?: "analog" | "digital"; // digital or analog IOs selected, or none yet
  selectedIoAggregationType?: AggregationType; // aggregation type for analog IOs
  tableSort: {
    [tableName: string]: ITableSort;
  };
  trendMember: "energy" | "power" | "costs";
  chargingSessionFilter: IChargingSessionFilter;
  rfidList: string[];
  reportLinks: (IReportState | null)[];
  reportData: IReportData | null;
  reportOptions: IReportOptions;
  chargerCmd: IChargerCmdState;
  login: IStateGuiLogin;
  lastFileUploadError?: string;
  fileUploads: ImportFile[];
}

/* initial state */
export const INITIAL_STATE: IStateGui = {
  navigationIsOpen: false,
  isDropdownOpen: {},
  tariffId: 0,
  chargingSessionFilter: {
    startDate: undefined,
    timePeriod: TimePeriod.MONTH,
  },
  rfidList: [],
  reportOptions: {},
  reportLinks: [],
  reportData: {
    requested: 0,
  },
  trendPeriod: {
    timePeriod: TimePeriod.DAY,
  },
  ioPeriod: {
    timePeriod: TimePeriod.DAY,
  },
  tableSort: {},
  trendMember: "energy",
  modal: {
    panel: ModalPanel.NONE,
    dataState: DataState.EMPTY,
  },
  confirmModal: {
    isOpen: false,
  },
  modalInput: undefined,
  modalOutput: undefined,
  modalTrendData: undefined,
  modalReport: undefined,
  modalTrendMonitor: undefined,
  modalCharger: undefined,
  modalRfid: undefined,
  modalTariff: undefined,
  chargerCmd: {
    state: OcppCmdState.NONE,
    hasError: false,
    chargerId: undefined,
    timestampMs: undefined,
  },
  login: {},
  fileUploads: [],
};
